// ===========================================
// ClientsCards — Top clients par CA + derniers clients ajoutés
// ===========================================
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Empty, Skeleton, Tag } from 'antd';
import { TrophyOutlined, UserAddOutlined } from '@ant-design/icons';
import { formatMontant, formatDate, getInitials } from '../../../common/utils/format';

const getClientLabel = (client) => {
    if (!client) return '—';
    if (client.raisonSociale) return client.raisonSociale;
    return `${client.prenom || ''} ${client.nom || ''}`.trim() || client.code || '—';
};

const ClientRow = ({ client, trailing, rank }) => {
    const navigate = useNavigate();
    const label = getClientLabel(client);
    const isEntreprise = client?.type === 'entreprise' || !!client?.raisonSociale;
    const initials = isEntreprise
        ? (client.raisonSociale || '?').slice(0, 2).toUpperCase()
        : getInitials(client?.prenom, client?.nom) || '?';

    return (
        <li
            className="clients-row"
            onClick={() => navigate(`/dashboard/client/${client._id}`)}
            role="button"
            tabIndex={0}
        >
            {rank && <div className="clients-row-rank">#{rank}</div>}
            <div className="clients-row-avatar" aria-hidden="true">{initials}</div>
            <div className="clients-row-body">
                <div className="clients-row-name">{label}</div>
                <div className="clients-row-meta">
                    <Tag color={isEntreprise ? 'blue' : 'green'}>
                        {isEntreprise ? 'Entreprise' : 'Particulier'}
                    </Tag>
                    {client.telephone && (
                        <span className="clients-row-phone">{client.telephone}</span>
                    )}
                </div>
            </div>
            <div className="clients-row-trailing">{trailing}</div>
        </li>
    );
};

export const TopClients = ({ data, loading }) => {
    return (
        <Card
            className="dashboard-op-card"
            title={
                <div className="chart-card-title">
                    <TrophyOutlined />
                    <span>Top clients (CA généré)</span>
                </div>
            }
        >
            <div className="op-list-wrapper">
                {loading ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : !data || data.length === 0 ? (
                    <Empty description="Aucun client" image={Empty.PRESENTED_IMAGE_SIMPLE} className="op-empty" />
                ) : (
                    <ul className="op-list clients-list">
                        {data.map((c, idx) => (
                            <ClientRow
                                key={c._id}
                                client={c}
                                rank={idx + 1}
                                trailing={
                                    <div className="clients-row-amount">
                                        {formatMontant(c.totalCa || 0)} F
                                        <span className="clients-row-amount-sub">{c.nbReglements} versement{c.nbReglements > 1 ? 's' : ''}</span>
                                    </div>
                                }
                            />
                        ))}
                    </ul>
                )}
            </div>
        </Card>
    );
};

export const RecentClients = ({ data, loading }) => {
    return (
        <Card
            className="dashboard-op-card"
            title={
                <div className="chart-card-title">
                    <UserAddOutlined />
                    <span>Derniers clients ajoutés</span>
                </div>
            }
        >
            <div className="op-list-wrapper">
                {loading ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : !data || data.length === 0 ? (
                    <Empty description="Aucun client récent" image={Empty.PRESENTED_IMAGE_SIMPLE} className="op-empty" />
                ) : (
                    <ul className="op-list clients-list">
                        {data.map((c) => (
                            <ClientRow
                                key={c._id}
                                client={c}
                                trailing={
                                    <div className="clients-row-amount-sub">
                                        {formatDate(c.createdAt)}
                                    </div>
                                }
                            />
                        ))}
                    </ul>
                )}
            </div>
        </Card>
    );
};
